import AsyncSelect from '@/components/Form/Shared/AsyncSelect'
import EmployeeSelect from '@/components/Form/Shared/EmployeeSelect'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import AddIcons from '@/icons/AddIcons'
import { createCustomerJourney } from '@/modules/Customer/services'
import { getProductsGroupOptions } from '@/modules/ProductGroup/services'
import useAuthStore from '@/stores/useAuthStore'
import { ModalProps } from '@/types/shared'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Dialog, GridHookForm } from 'vnetwork-ui'
import { AddCustomerJourneySchema } from './schema'

export interface AddCustomerJourneyProps extends ModalProps {
    customerId: number
    contact_id: number
    isUpsell?: boolean
    hasSuccessfulJourney?: boolean
}

const AddCustomerJourney: React.FC<AddCustomerJourneyProps> = ({
    onClose,
    open,
    customerId,
    contact_id,
    isUpsell = false,
    hasSuccessfulJourney = false,
}) => {
    const { employee } = useAuthStore()

    const { t } = useTranslation()

    const formControl = useForm({
        defaultValues: {
            customer_journey_explaination: {
                sale_tracking_id: [employee?.employee_id],
                service_interestings: [],
            },
        },
        resolver: yupResolver(AddCustomerJourneySchema),
    })

    const { mutateAsync, isPending } = useMutationWithHandler(
        {
            mutationFn: createCustomerJourney,
        },
        {
            reactQueryKeys: [
                QUERY_KEYS.CUSTOMERS,
                QUERY_KEYS.CUSTOMERS_JOURNEYS,
            ],
            onSuccess: () => {
                onClose()
            },
        }
    )

    const handleAddCustomerJourney = (values: any) => {
        mutateAsync({
            customer_id: customerId,
            params: {
                contact_id,
                ...values,
            },
        })
    }

    return (
        <Dialog
            onClose={onClose}
            open={open}
            children={
                <>
                    {/* Alert for upsell info */}
                    <div
                        className={`mb-4 p-3 rounded-lg border ${
                            isUpsell
                                ? 'bg-violet-50 border-violet-200'
                                : 'bg-blue-50 border-blue-200'
                        }`}
                    >
                        <div className="flex items-start gap-2">
                            <div
                                className={`mt-0.5 ${isUpsell ? 'text-violet-500' : 'text-blue-500'}`}
                            >
                                <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                    />
                                </svg>
                            </div>
                            <div>
                                <p
                                    className={`text-sm font-medium ${isUpsell ? 'text-violet-700' : 'text-blue-700'}`}
                                >
                                    {isUpsell
                                        ? t('journey-type-upsell')
                                        : t('journey-type-new-sale')}
                                </p>
                                <p
                                    className={`text-xs mt-0.5 ${isUpsell ? 'text-violet-600' : 'text-blue-600'}`}
                                >
                                    {isUpsell
                                        ? t('journey-upsell-description')
                                        : hasSuccessfulJourney
                                          ? t(
                                                'journey-new-sale-after-success-description'
                                            )
                                          : t('journey-new-sale-description')}
                                </p>
                            </div>
                        </div>
                    </div>
                    <GridHookForm
                        actions={[
                            {
                                title: t('cancel'),
                                type: 'button',
                                handler: onClose,
                                variant: 'outlined',
                                disabled: isPending,
                            },
                            {
                                title: t('add-customer-journey'),
                                type: 'submit',
                                startIcon: <AddIcons />,
                                variant: 'contained',
                                disabled: isPending,
                                loading: isPending,
                            },
                        ]}
                        data={[
                            {
                                renderEditComponent: (control) => {
                                    return (
                                        <AsyncSelect
                                            asyncOptions={
                                                getProductsGroupOptions
                                            }
                                            control={control}
                                            name="customer_journey_explaination.service_interestings"
                                            placeholder={t(
                                                'customer_service_interestings'
                                            )}
                                            allowClear={false}
                                            multiple
                                            label={t(
                                                'customer_service_interestings'
                                            )}
                                        />
                                    )
                                },
                                className: 'md:col-span-4 col-span-1',
                            },
                            {
                                renderEditComponent: (control) => {
                                    return (
                                        <EmployeeSelect
                                            control={control}
                                            name="customer_journey_explaination.sale_tracking_id"
                                            multiple
                                        />
                                    )
                                },
                                className: 'md:col-span-4 col-span-1',
                            },
                        ]}
                        formControl={formControl}
                        onSubmit={handleAddCustomerJourney}
                    />
                </>
            }
            title={t('add-customer-journey')}
        />
    )
}

export default AddCustomerJourney
